import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomSignupField } from '@shared/welibrary-graphql/types';
import { useGetMultipleJoinGroupInfoQuery } from '@shared/welibrary-graphql/user/queries.hook';
import {
    useAddUserToGroupMutation,
    useJoinOrRequestToJoinMultipleGroupsByIdMutation,
} from '@shared/welibrary-graphql/user/mutations.hook';
import GroupSelectItem from './GroupSelectItem';
import CaretDownIcon from '@dsc/svgs/CaretDownIcon';
import { useCurrentUser } from '@stores/User';
import { useToastActionsContext } from '@components/toast/NewToastContext';
import AlertToast from '@components/toast/AlertToast';
import useModal from '@web/ui/components/modals/hooks/useModal';
import ConfirmationModal from './ConfirmationModal';

type GroupSelectProps = {
    field: CustomSignupField;
    membershipGroupId: string;
};

const GroupSelect: React.FC<GroupSelectProps> = ({ field, membershipGroupId }) => {
    const [open, setOpen] = useState(false);
    const { currentUser } = useCurrentUser();
    const { newToast } = useToastActionsContext();
    const { t } = useTranslation();
    const [addUserToGroup] = useAddUserToGroupMutation();
    const [joinGroups] = useJoinOrRequestToJoinMultipleGroupsByIdMutation();

    const {
        data,
        loading: groupInfoLoading,
        refetch,
    } = useGetMultipleJoinGroupInfoQuery({
        variables: { ids: field.groups?.map(group => group.group?._id ?? group.id) },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
    });

    const [groups, setGroups] = useState(field.groups || []);
    const { newModal, closeModal } = useModal();

    useEffect(() => {
        if (data?.groupsById) {
            const updatedGroups =
                field.groups?.map(group => ({
                    ...group,
                    group:
                        data.groupsById.find(resolvedGroup => resolvedGroup._id === group.id) ??
                        group.group,
                })) ?? [];
            setGroups(updatedGroups);
        }
    }, [data, field.groups]);

    const sortedGroups = [...groups].sort((groupA, groupB) => {
        if (groupA.group?.currentUserIsMember && !groupB.group?.currentUserIsMember) return -1;
        if (!groupA.group?.currentUserIsMember && groupB.group?.currentUserIsMember) return 1;

        if (groupA.group?.currentUserIsMember && groupB.group?.currentUserIsMember) {
            return groupA.group.profile?.full_name > groupB.group.profile?.full_name ? 1 : -1;
        }

        return 0;
    });

    const handleGroupToggle = async (groupId: string, isMember: boolean) => {
        if (isMember) {
            const confirmationMessage = t(
                'common:confirm_leave_group',
                "Unselecting this group means you'll leave the group and lose access to it. Are you sure you want to continue?"
            );

            newModal(
                <ConfirmationModal
                    message={confirmationMessage}
                    onConfirm={() => {
                        closeModal();
                        leaveGroup(groupId);
                    }}
                    onCancel={closeModal}
                />,
                { hideButton: true }
            );
        } else {
            joinGroup(groupId);
        }
    };

    const leaveGroup = async (groupId: string) => {
        try {
            // Optimistically update the local state
            setGroups(prevGroups =>
                prevGroups.map(group =>
                    group.id === groupId
                        ? { ...group, group: { ...group.group, currentUserIsMember: false } }
                        : group
                )
            );

            const response = await addUserToGroup({
                variables: {
                    groupId,
                    userId: currentUser._id,
                    remove: true,
                },
            });

            if (response.data?.addUserToGroup) {
                newToast(
                    <AlertToast
                        boldText={t('common:group_left', 'Group Left')}
                        text={t('common:you_have_left_the_group', 'You have left the group.')}
                        showSuccessIcon
                    />,
                    { duration: 5000 }
                );
            } else {
                // If the server operation failed, revert the local state
                setGroups(prevGroups =>
                    prevGroups.map(group =>
                        group.id === groupId
                            ? { ...group, group: { ...group.group, currentUserIsMember: true } }
                            : group
                    )
                );
                throw new Error('Failed to leave group');
            }
        } catch (error) {
            console.error('Error leaving group:', error);
            let errorMessage = t(
                'common:error_leaving_group',
                'An error occurred while leaving the group.'
            );

            if (error.message.includes('User not found')) {
                errorMessage = t(
                    'common:user_not_found',
                    'There was an issue updating your membership. Please try again.'
                );
            }
        } finally {
            // Always refetch to ensure consistency with the server
            refetch();
        }
    };

    const joinGroup = async (groupId: string) => {
        try {
            const res = await joinGroups({ variables: { groupIDs: [groupId], membershipGroupId } });
            if (res.data?.joinOrRequestToJoinMultipleGroupsByID?.[0]?.status === 'groupJoined') {
                setGroups(prevGroups =>
                    prevGroups.map(group =>
                        group.id === groupId
                            ? { ...group, group: { ...group.group, currentUserIsMember: true } }
                            : group
                    )
                );

                refetch();

                newToast(
                    <AlertToast
                        boldText={t('common:group_joined', 'Group Joined')}
                        text={t('common:you_have_joined_the_group', 'You have joined the group.')}
                        showSuccessIcon
                    />,
                    { duration: 5000 }
                );
            } else if (
                res.data?.joinOrRequestToJoinMultipleGroupsByID?.[0]?.status ===
                'groupRequestToJoin'
            ) {
                newToast(
                    <AlertToast
                        boldText={t('common:request_sent', 'Request Sent')}
                        text={t(
                            'common:your_request_to_join_the_group_has_been_sent',
                            'Your request to join the group has been sent.'
                        )}
                        showSuccessIcon
                    />,
                    { duration: 5000 }
                );
            }
        } catch (error) {
            console.error('Error joining group:', error);
            newToast(
                <AlertToast
                    boldText={t('common:error_title', 'Error')}
                    text={t(
                        'common:error_joining_group',
                        'An error occurred while joining the group.'
                    )}
                    showErrorIcon
                />,
                { duration: 5000 }
            );
        }
    };

    if (sortedGroups.length === 0) return null;

    return (
        <section>
            {field.prompt && <p>{field.prompt}</p>}

            <section className="mb-4 w-full rounded-[15px] border border-black bg-[--color-grayscale-input-background]">
                <button
                    type="button"
                    className={`flex w-full items-center justify-between px-[17px] py-[20px] text-black ${
                        open ? 'border-b border-b-black' : ''
                    }`}
                    onClick={() => setOpen(!open)}
                >
                    <p className="font-poppins m-0 p-0 text-base text-black">Select</p>
                    <CaretDownIcon className={`transition-all ${open ? 'rotate-180' : ''}`} />
                </button>

                <ul
                    className={`grid grid-cols-[repeat(auto-fit,_minmax(200px,_1fr))] gap-[10px] overflow-y-auto transition-all ${
                        open ? 'max-h-96 px-[17px] py-[20px]' : 'max-h-0 scale-0 opacity-0'
                    }`}
                >
                    {sortedGroups.map(group => (
                        <GroupSelectItem
                            groupId={group.id}
                            group={group.group}
                            loading={groupInfoLoading}
                            membershipGroupId={membershipGroupId}
                            skipApproval={!group.requireApproval}
                            key={group.id}
                            onToggle={handleGroupToggle}
                        />
                    ))}
                </ul>
            </section>
        </section>
    );
};

export default GroupSelect;
